import { ArrowLeft, ArrowRight, CircleAlert, Plus, RefreshCw } from "lucide-react";
import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import GoogleSignInButton from "../../components/GoogleSignInButton";
import type { LastLoginPreference } from "../../utils/lastLoginPreference";
import LastUsedBadge from "./LastUsedBadge";

interface PartnerLoginViewProps {
  oauthQuery?: string;
  step: "email" | "otp";
  email: string;
  otp: string;
  error: string;
  isLoading: boolean;
  resendCooldown: number;
  googleSignInEnabled: boolean;
  lastLoginMethod?: LastLoginPreference["method"];
  onEmailChange: (email: string) => void;
  onOtpChange: (otp: string) => void;
  onEmailSubmit: (event: React.FormEvent) => void;
  onOtpSubmit: (event: React.FormEvent) => void;
  onResendOtp: () => void;
  onBackToEmail: () => void;
  onSupportClick: (placement: "login") => void;
}

export default function PartnerLoginView({
  oauthQuery,
  step,
  email,
  otp,
  error,
  isLoading,
  resendCooldown,
  googleSignInEnabled,
  lastLoginMethod,
  onEmailChange,
  onOtpChange,
  onEmailSubmit,
  onOtpSubmit,
  onResendOtp,
  onBackToEmail,
  onSupportClick,
}: PartnerLoginViewProps) {
  const isOtp = step === "otp";

  useEffect(() => {
    // Reuse the public share page's launcher policy; support remains available inline.
    const root = document.documentElement;
    const previousValue = root.getAttribute("data-alpha-intercom-launcher-hidden");
    root.setAttribute("data-alpha-intercom-launcher-hidden", "true");
    return () => {
      if (previousValue === null) {
        root.removeAttribute("data-alpha-intercom-launcher-hidden");
      } else {
        root.setAttribute("data-alpha-intercom-launcher-hidden", previousValue);
      }
    };
  }, []);

  useEffect(() => {
    const themeMeta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
    const previousThemeColor = themeMeta?.getAttribute("content") ?? null;
    const backgroundElements = [document.documentElement, document.body, document.getElementById("root")]
      .filter((element): element is HTMLElement => element !== null);
    const previousCanvasClasses = backgroundElements.map(element => element.classList.contains("bg-v7-canvas"));

    backgroundElements.forEach(element => element.classList.add("bg-v7-canvas"));
    const canvasColor = window.getComputedStyle(document.body).backgroundColor;
    if (themeMeta && canvasColor) themeMeta.setAttribute("content", canvasColor);

    return () => {
      backgroundElements.forEach((element, index) => {
        if (!previousCanvasClasses[index]) element.classList.remove("bg-v7-canvas");
      });
      if (themeMeta) {
        if (previousThemeColor === null) themeMeta.removeAttribute("content");
        else themeMeta.setAttribute("content", previousThemeColor);
      }
    };
  }, []);

  return (
    <div className="grid min-h-screen min-h-dvh bg-v7-canvas pb-[env(safe-area-inset-bottom)] pl-[env(safe-area-inset-left)] pr-[env(safe-area-inset-right)] pt-[env(safe-area-inset-top)] font-v7-sans text-v7-ink transition-colors duration-300 lg:grid-cols-[minmax(380px,0.88fr)_minmax(560px,1.12fr)]">
      <a
        href="#partner-login"
        className="fixed left-4 top-4 z-50 -translate-y-24 rounded-v7-control border border-v7-ink bg-v7-white px-4 py-3 text-[13px] font-medium text-v7-ink focus:translate-y-0 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-v7-bronze-dark"
      >
        Skip to sign in
      </a>

      <section
        aria-label="MM and OPm"
        className="relative isolate flex min-h-[360px] min-w-0 flex-col overflow-hidden bg-v7-ink px-5 py-6 text-v7-white sm:px-8 lg:min-h-screen lg:min-h-[100dvh] lg:px-12 lg:py-9"
      >
        <div className="pointer-events-none absolute -left-32 -top-40 h-96 w-96 rounded-full bg-v7-bronze/10 blur-3xl" aria-hidden="true" />
        <div className="pointer-events-none absolute -bottom-40 -right-40 h-96 w-96 rounded-full bg-v7-white/[0.04] blur-3xl" aria-hidden="true" />
        <Link
          to="/"
          aria-label="Powered by Plutus"
          className="relative z-10 inline-flex min-h-11 w-fit items-center gap-3 text-xs text-v7-white/70 transition-colors hover:text-v7-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-v7-white"
        >
          <span>Powered by</span>
          <img src="/favicon.png" alt="" width={36} height={36} className="h-9 w-9 object-contain brightness-0 invert" />
          <span className="font-medium tracking-[0.24em] text-v7-white">PLUTUS</span>
        </Link>

        <div className="relative z-10 mx-auto my-auto w-full max-w-[440px] py-10 lg:py-16">
          <div className="mx-auto grid max-w-[440px] grid-cols-[1fr_auto_1fr] items-center gap-3 sm:gap-4 lg:grid-cols-2 lg:items-start">
            <img
              src="/partner-logos/mm.png"
              alt="MM."
              width={500}
              height={500}
              className="aspect-square w-full rounded-v7-control object-contain"
            />
            <Plus className="h-4 w-4 text-v7-white/40 lg:hidden" aria-hidden="true" />
            <img
              src="/partner-logos/opm.png"
              alt="OPm."
              width={500}
              height={500}
              className="aspect-square w-full rounded-v7-control object-contain lg:mt-14"
            />
          </div>

          <div className="my-6 flex items-center gap-4 lg:my-8">
            <span className="h-px flex-1 bg-v7-white/20" aria-hidden="true" />
            <p className="text-[11px] font-medium uppercase leading-[14px] tracking-[0.14em] text-v7-white/70">As seen on</p>
            <span className="h-px flex-1 bg-v7-white/20" aria-hidden="true" />
          </div>

          <div>
            <h2 className="text-[42px] font-light leading-[0.98] tracking-[-0.055em] sm:text-5xl lg:text-[56px]">45 days <span className="font-v7-serif font-normal italic">complimentary.</span></h2>
            <p className="mt-5 text-[15px] leading-[22px] text-v7-white/70">
              Let support know the code{" "}
              <span className="font-mono font-medium text-v7-white">MM45</span>{" "}
              or{" "}
              <span className="font-mono font-medium text-v7-white">OPM45</span>{" "}
              to get 45 days complimentary.
            </p>
          </div>
        </div>
      </section>

      <main id="partner-login" aria-labelledby="partner-login-title" className="flex min-w-0 scroll-mt-6 flex-col bg-v7-canvas px-4 pb-8 pt-5 sm:px-8 lg:min-h-screen lg:min-h-[100dvh] lg:px-10 lg:py-8">
        <div className="mx-auto w-full max-w-[470px] py-12 lg:my-auto lg:py-10">
          <div className="mb-8">
            <img src="/favicon.png" alt="Plutus" width={40} height={40} className="mb-6 h-10 w-10 object-contain" />
            <h1 id="partner-login-title" className="text-[38px] font-light leading-none tracking-[-0.05em] text-v7-ink sm:text-[44px] lg:text-[52px]">
              {isOtp ? (
                <>Check your <span className="font-v7-serif font-normal italic">email.</span></>
              ) : (
                <>Login or create an <span className="font-v7-serif font-normal italic">account</span></>
              )}
            </h1>
            <p id="partner-login-description" className="mt-4 break-words text-[15px] leading-[22px] text-v7-ink-soft" aria-live="polite">
              {isOtp ? (
                <>Enter the 6-digit code sent to <span className="font-medium text-v7-ink">{email}</span>.</>
              ) : (
                "Simply enter your email below."
              )}
            </p>
          </div>

          <div className="rounded-v7-panel border border-v7-ink/15 bg-v7-white p-4 sm:p-6">
            <form onSubmit={isOtp ? onOtpSubmit : onEmailSubmit} className="space-y-5" aria-busy={isLoading}>
              {isOtp ? (
                <div>
                  <div className="mb-2 flex items-center justify-between gap-3">
                    <label htmlFor="partner-otp" className="text-[13px] leading-[19px] text-v7-ink-soft">One-time code</label>
                    <button
                      type="button"
                      onClick={onBackToEmail}
                      disabled={isLoading}
                      className="inline-flex min-h-11 items-center gap-1.5 text-xs text-v7-ink-soft transition-colors hover:text-v7-ink focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-v7-bronze-dark disabled:cursor-not-allowed disabled:text-v7-ink/40"
                    >
                      <ArrowLeft className="h-3.5 w-3.5" aria-hidden="true" />
                      Edit email
                    </button>
                  </div>
                  <input
                    id="partner-otp"
                    name="otp"
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    autoFocus
                    required
                    maxLength={6}
                    value={otp}
                    onChange={event => onOtpChange(event.target.value.replace(/\D/g, ""))}
                    disabled={isLoading}
                    placeholder="000000"
                    aria-invalid={Boolean(error)}
                    aria-describedby={`partner-login-description${error ? " partner-auth-error" : ""}`}
                    className="min-h-14 w-full rounded-v7-control border border-v7-ink/30 bg-v7-white px-3 text-center font-mono text-2xl tracking-[0.4em] text-v7-ink outline-none placeholder:text-v7-ink/30 transition-colors duration-200 hover:border-v7-ink/60 focus:border-v7-bronze-dark focus:ring-2 focus:ring-v7-bronze-dark/15 disabled:cursor-not-allowed disabled:bg-v7-mist disabled:text-v7-ink/40"
                  />
                </div>
              ) : (
                <div>
                  <div className="mb-2 flex flex-wrap items-center gap-2">
                    <label htmlFor="partner-email" className="text-[13px] leading-[19px] text-v7-ink-soft">Email address</label>
                    {lastLoginMethod === "email" && <LastUsedBadge />}
                  </div>
                  <input
                    id="partner-email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    required
                    value={email}
                    onChange={event => onEmailChange(event.target.value.toLowerCase())}
                    disabled={isLoading}
                    placeholder="you@example.com"
                    aria-invalid={Boolean(error)}
                    aria-describedby={error ? "partner-auth-error" : undefined}
                    className="min-h-12 w-full rounded-v7-control border border-v7-ink/30 bg-v7-white px-3 text-base leading-[22px] text-v7-ink outline-none placeholder:text-v7-ink/40 transition-colors duration-200 hover:border-v7-ink/60 focus:border-v7-bronze-dark focus:ring-2 focus:ring-v7-bronze-dark/15 disabled:cursor-not-allowed disabled:bg-v7-mist disabled:text-v7-ink/40 sm:text-[15px]"
                  />
                </div>
              )}

              {error && (
                <div id="partner-auth-error" role="alert" className="flex items-start gap-2 rounded-v7-control border border-red-200 bg-red-50 p-3 text-[13px] leading-[19px] text-red-700">
                  <CircleAlert className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
                  <span className="break-words">{error}</span>
                </div>
              )}

              <button
                type="submit"
                disabled={isLoading || (isOtp && otp.length !== 6)}
                className="flex min-h-12 w-full items-center justify-center gap-2 rounded-v7-control border border-v7-ink bg-v7-ink px-[18px] text-[13px] font-medium tracking-[0.06em] text-v7-white transition-colors duration-200 hover:bg-v7-ink-soft focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-v7-bronze-dark disabled:cursor-not-allowed disabled:border-v7-ink/15 disabled:bg-v7-mist disabled:text-v7-ink/40"
              >
                {isLoading ? (
                  <><RefreshCw className="h-4 w-4 animate-spin motion-reduce:animate-none" aria-hidden="true" />{isOtp ? "Verifying..." : "Sending code..."}</>
                ) : (
                  <>{isOtp ? "Verify & continue" : "Continue with email"}<ArrowRight className="h-4 w-4" aria-hidden="true" /></>
                )}
              </button>

              {isOtp ? (
                <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-v7-ink/60">
                  <span>Check your spam folder, too.</span>
                  <button
                    type="button"
                    onClick={onResendOtp}
                    disabled={isLoading || resendCooldown > 0}
                    className="min-h-11 font-medium text-v7-ink-soft underline underline-offset-4 transition-colors hover:text-v7-ink focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-v7-bronze-dark disabled:cursor-not-allowed disabled:text-v7-ink/40 disabled:no-underline"
                  >
                    {resendCooldown > 0
                      ? `Resend in ${Math.floor(resendCooldown / 60)}:${(resendCooldown % 60).toString().padStart(2, "0")}`
                      : "Resend code"}
                  </button>
                </div>
              ) : (
                <p className="text-center text-xs leading-[17px] text-v7-ink/60">
                  We will email you a sign-in code. No password needed.
                </p>
              )}
            </form>

            {!isOtp && googleSignInEnabled && (
              <div className="mt-6">
                <div className="mb-6 flex items-center gap-4" aria-hidden="true">
                  <span className="h-px flex-1 bg-v7-ink/15" />
                  <span className="text-xs text-v7-ink/60">or</span>
                  <span className="h-px flex-1 bg-v7-ink/15" />
                </div>
                {lastLoginMethod === "google" && (
                  <div className="mb-2 flex justify-end"><LastUsedBadge /></div>
                )}
                <GoogleSignInButton oauthQuery={oauthQuery} />
              </div>
            )}
          </div>

          <p className="mt-8 border-t border-v7-ink/15 pt-6 text-center text-xs text-v7-ink/60">
            Need a hand?{" "}
            <a href="mailto:hello@runplutus.com" onClick={() => onSupportClick("login")} className="inline-flex min-h-11 items-center font-medium text-v7-ink-soft underline underline-offset-4 transition-colors hover:text-v7-ink focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-v7-bronze-dark">Contact support</a>
          </p>
          <footer className="mt-5 text-[11px] font-light leading-[17px] text-v7-ink/45">
            <p>
              By continuing, you agree to our{" "}
              <a href="/terms" className="text-v7-ink-soft underline underline-offset-4 transition-colors hover:text-v7-ink focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-v7-bronze-dark">Terms</a>{" "}
              and{" "}
              <a href="/privacy" className="text-v7-ink-soft underline underline-offset-4 transition-colors hover:text-v7-ink focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-v7-bronze-dark">Privacy Policy</a>. Advisory services by Namsoft Advisors LLC, an SEC-registered investment advisor.
            </p>
          </footer>
        </div>
      </main>
    </div>
  );
}
